<script setup lang="ts">
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import DButton from '../../design/components/DButton.vue';
import FormError from '../plans/FormError.vue';
import DDialog from '../../design/components/DDialog.vue';
import DField from '../../design/components/DField.vue';
import DInput from '../../design/components/DInput.vue';
import { useFormSubmit } from '../../app/composables/useFormSubmit.ts';
import { useWorkspaceStore } from '../../app/stores/workspace.ts';

const open = defineModel<boolean>('open', { required: true });
const workspace = useWorkspaceStore();
const router = useRouter();
const { pending, formError, atYourLimit, fieldErrors, submit, reset } = useFormSubmit();
const name = ref('');

watch(open, (isOpen) => {
  if (isOpen) {
    name.value = '';
    reset();
  }
});

async function onSubmit() {
  let slug = '';
  const ok = await submit(async () => {
    slug = (await workspace.createOrg(name.value)).slug;
  });
  if (ok) {
    open.value = false;
    await router.push({ name: 'org', params: { org: slug } });
  }
}
</script>

<template>
  <DDialog
    v-model:open="open"
    title="New organization"
    description="A shared space for a team: its own projects, members and settings."
  >
    <FormError
      v-if="formError"
      :message="formError"
      :at-your-limit="atYourLimit"
      @navigate="open = false"
    />
    <form class="form" novalidate @submit.prevent="onSubmit">
      <DField v-slot="field" label="Name" :error="fieldErrors['name']">
        <DInput
          v-bind="field"
          v-model="name"
          maxlength="100"
          autocomplete="organization"
          required
        />
      </DField>
      <div class="dialog__actions">
        <DButton @click="open = false">Cancel</DButton>
        <DButton type="submit" variant="primary" :loading="pending">{{
          pending ? 'Creating…' : 'Create organization'
        }}</DButton>
      </div>
    </form>
  </DDialog>
</template>
